import { AxiosError } from 'axios';
import { useMutation, useQueryClient } from 'react-query';
import { Axios } from '@utils/api/axios-instance';
import { BaseError } from '@utils/types/shared/base';
import {
  AdminTranslationSettingsResponse,
  AdminTranslationSettingsUpdate
} from '@utils/types/admin';
import { ADMIN_TRANSLATION_QUERY_KEY } from './use-get-admin-translation';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

/**
 * Hook to update translation settings configuration
 *
 * PUT /api/admin/settings/translation
 *
 * Updates DeepL settings (enabled, API key, API URL)
 */
export const useUpdateAdminTranslation = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const updateTranslation = async (payload: AdminTranslationSettingsUpdate) => {
    const url = `api/admin/settings/translation`;
    const response = await Axios.put<AdminTranslationSettingsResponse>(
      url,
      payload
    );
    return response.data;
  };

  return useMutation<
    AdminTranslationSettingsResponse,
    AxiosError<BaseError>,
    AdminTranslationSettingsUpdate
  >(updateTranslation, {
    onSuccess: () => {
      toast.success(t('translation-settings-saved'));
      queryClient.invalidateQueries([ADMIN_TRANSLATION_QUERY_KEY]);
    },
    onError: (error: AxiosError<BaseError>) => {
      const errorMessage =
        error.response?.data?.error_description ||
        error.response?.data?.detail ||
        error.response?.data?.error ||
        error.response?.data?.message ||
        t('failed-to-save-translation-settings');
      toast.error(errorMessage);
    }
  });
};
